import { useState , useEffect, lazy, Suspense} from 'react';
import SideBar, { menuItems } from './components/SideBar/SideBar';
import Header from './components/Header/Header';
import ProductionManagement from './pages/ProductionManagement/ProductionManagement';
import ContratManagement from './pages/ContractManagement/ContractManagement';
import ProductManagement from './pages/ProductManagement/ProductManagement';
import ProcessManagement from './pages/ProcessManagement/ProcessManagement';
import MaterialManagement from './pages/MaterialManagement/MaterialManagement';
import ProductStockManagement from './pages/ProductStockManagement/ProductStockManagement';
import MachineManagement from './pages/MachineManagement/MachineManagement';
import PartnerManagement from './pages/PartnerManagement/PartnerManagement';
import Login from './pages/Login/Login';
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { useSession } from './stores/session/SessionAction';
import { useDispatch, useSelector } from 'react-redux';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import KPI from './pages/KPI/KPI';
import UpTime from './pages/UpTime/UpTime';
import Dashboard from './pages/Dashboard/Dashboard';
import KpiAdmin from './pages/KpiAdmin/KpiAdmin';
import AdminRoute from './components/AdminRoute/AdminRoute';
import KpiUpdate from './pages/KpiUpdate/KpiUpdate';
import DashboardIqCluster from './pages/DashboardIqCluster/DashboardIqCluster';
import AutoLoginAsCompany from './pages/AutoLoginAsCompany/AutoLoginAsCompany';

const AdminSensor = lazy(() => import('./pages/AdminSensor/AdminSensor'));
const AdminCompany = lazy(() => import('./pages/AdminCompany/AdminCompany'));
const AdminIndex = lazy(() => import('./pages/AdminIndex/AdminIndex'));
const AdminKpiIndicator = lazy(() => import('./pages/AdminKpiIndicator/AdminKpiIndicator'));
const AdminDashboardSettings = lazy(() => import('./pages/AdminDashboardSettings/AdminDashboardSettings'));


function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [selectedMenu, setSelectedMenu] = useState(Object.keys(menuItems)[0]);
  const session = useSelector((state) => state.session.data);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
      else setIsSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const routeMap = {
    '생산 관리': '/production',
    'KPI' : '/kpi',
    '수주 관리': '/contract',
    '제품 관리': '/product',
    '공정 관리': '/process',
    '자재 재고 관리': '/material',
    '완제품 재고 관리': '/product-stock',
    '설비 관리': '/machine',
    '거래처 관리': '/partner',
    '대시보드': '/dashboard',
    '스마트라인플러스' : ''
  };

  const handleMenuSelect = (menu, navigate) => {
    setSelectedMenu(menu);
    if (isMobile) setIsSidebarOpen(false);
    if (menu === '스마트라인플러스') {
      const url = session.plus;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (menu === '대시보드') {
      navigate(session.dashboardPath || '/dashboard');
      return;
    }
    navigate(routeMap[menu]);
  };

  const handleHomeClick = (navigate) => {
    navigate(session.dashboardPath || routeMap['대시보드']);
    setSelectedMenu(Object.keys(menuItems)[0]);
  };

  return (
    <Router>
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading…</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/__open-as-company" element={<AutoLoginAsCompany />} />
        {/* 관리자 화면: is_admin 계정만. 미로그인 → /login, 비관리자 → 본인 대시보드 */}
        <Route path="/admin" element={<AdminRoute><AdminIndex /></AdminRoute>} />
        <Route path="/admin/sensor" element={<AdminRoute><AdminSensor /></AdminRoute>} />
        <Route path="/admin/company" element={<AdminRoute><AdminCompany /></AdminRoute>} />
        <Route path="/admin/kpi" element={<AdminRoute><AdminKpiIndicator /></AdminRoute>} />
        <Route path="/admin/dashboard-settings" element={<AdminRoute><AdminDashboardSettings /></AdminRoute>} />
        <Route path="/kpi/update" element={<AdminRoute><KpiUpdate /></AdminRoute>} />
        <Route path="/*" element={
          <AppContent
            isSidebarOpen={isSidebarOpen}
            isMobile={isMobile}
            toggleSidebar={toggleSidebar}
            selectedMenu={selectedMenu}
            handleMenuSelect={handleMenuSelect}
            handleHomeClick={handleHomeClick}
          />
        } />
      </Routes>
      </Suspense>
    </Router>
  );
}

function AppContent({ isSidebarOpen, isMobile, toggleSidebar, selectedMenu, handleMenuSelect, handleHomeClick }) {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const sessionAPI = useSession();

  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        await Promise.resolve(dispatch(sessionAPI.request.get()));
      } catch (error) {
        console.error("Error fetching session data : ", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSessionData();
  }, [dispatch]);

  if (isLoading) return null;
  if (!session.data?.userSeq) return <Navigate to="/login" replace />;

  return (
    <div className="app">
      <div className="content-wrapper">
        {isMobile && isSidebarOpen && (
          <div className="sidebar-overlay" onClick={toggleSidebar} />
        )}
        <SideBar
          isOpen={isSidebarOpen}
          onSelectMenu={(menu) => handleMenuSelect(menu, navigate)}
          selectedMenu={ selectedMenu }
          onHomeClick={() => handleHomeClick(navigate)}
          session={ session }
        />
        <div className={`main-content ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
          <Header
            title={ selectedMenu }
            onHomeClick={ () => handleHomeClick(navigate) }
            onToggleSidebar={ toggleSidebar }
          />
          <div className="routable-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard-iq-cluster" element={<DashboardIqCluster />} />
              <Route path="/dashboard-smartbizmarker" element={<Dashboard />} />
              <Route path="/simplelineplus" element={<Dashboard />} />
              <Route path="/simplelineplus/kpi" element={<KPI />} />
              <Route path="/production" element={<ProductionManagement />} />
              <Route path="/kpi" element={<KPI />} />
              <Route path="/contract" element={<ContratManagement />} />
              <Route path="/product" element={<ProductManagement />} />
              <Route path="/process" element={<ProcessManagement />} />
              <Route path="/material" element={<MaterialManagement />} />
              <Route path="/product-stock" element={<ProductStockManagement />} />
              <Route path="/machine" element={<MachineManagement />} />
              <Route path="/partner" element={<PartnerManagement />} />
              <Route path="/power-monitor" element={<Navigate to="/dashboard" replace />} />
              <Route path="/ycyc" element={<UpTime />} />
              <Route path="/kpi-admin" element={<AdminRoute><KpiAdmin /></AdminRoute>} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;